import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, Swords as BattleIcon, Swords, Layers } from 'lucide-react';

import { env } from '@/env';
import { useTelegram } from '@/hooks/useTelegram';
import { useAppSelector } from '@/store/hooks';
import { useGameSelection } from '@/hooks/useGameSelection';
import { featuresForGame } from '@/lib/gameFeatures';
import { useGetFeaturedAvatarQuery } from '@/services/avatarApi';
import { GameHeader } from '@/components/games/GameHeader';
import { LeaderboardCart } from '@/components/hub/LeaderboardCart';
import { WatchPartyHub } from '@/components/hub/WatchPartyHub';
import { FeaturedAvatar } from '@/components/hub/FeaturedAvatar';
import { RiotHubTile } from '@/components/hub/RiotHubTile';
import { ChessHubSection } from '@/components/hub/ChessHubSection';
import { CocHubSection } from '@/components/hub/CocHubSection';
import { QbankHud } from '@/components/hub/QbankHud';
import { UserHeaderBanner } from '@/components/banner/UserHeaderBanner';
import { CustomizeSheet } from '@/components/banner/CustomizeSheet';
import { QueueSheet } from '@/components/social/QueueSheet';
import { ChromaKeyVideo } from '@/components/avatars/ChromaKeyVideo';
import { DualStreamVideo } from '@/components/avatars/DualStreamVideo';
import { supportsVp9Alpha } from '@/lib/videoAlphaSupport';

/**
 * Hub — single-viewport layout that never scrolls.
 *
 * Order:
 *   1. GameHeader         (active game + nickname + game tag, right-aligned
 *                          so the featured QAvatar's head can poke above the
 *                          banner on the left side without overlap)
 *   2. UserHeaderBanner   (banner is itself the tap target — opens
 *                          CustomizeSheet on tap, no pencil overlay)
 *      + featured QAvatar overlay anchored bottom-left of the banner; tap
 *      routes to /avatars
 *   3. Pills row          (leaderboard + watch party when live)
 */
export function Hub() {
  const telegram = useTelegram();
  const user = useAppSelector((s) => s.auth.user);
  const { activeGame } = useGameSelection();
  const navigate = useNavigate();
  const { t } = useTranslation('duels');
  const [customizeOpen, setCustomizeOpen] = useState(false);
  // Open-matchmaking searching sheet (Clash Royale 1v1). Mounted only while
  // open so its queue state machine resets per search.
  const [queueOpen, setQueueOpen] = useState(false);

  // Game-conditional Hub: only show a game's feature sections when that game
  // is the active selection (header picker → useGameSelection). Cross-game
  // surfaces (leaderboard, watch party, avatars) stay always-on. The env
  // feature flags remain an outer gate so a disabled feature never appears
  // even when its game is active.
  const features = featuresForGame(activeGame);
  const showCr = features.cr && env.featureClashRoyaleEnabled;
  const showRiot = features.riot && env.featureQriotsEnabled;
  const showChess = features.chess && env.featureChessEnabled;
  const showCoc = features.coc && env.featureCocEnabled;
  // Skip the avatar query entirely when avatars are disabled (PROD). Saves
  // a request per Hub mount and ensures `featured` is undefined so every
  // downstream avatar branch collapses without explicit gating.
  const { data: featured } = useGetFeaturedAvatarQuery(undefined, {
    skip: !env.featureAvatarsEnabled,
  });

  const fallback =
    user?.firstName ?? telegram.user?.first_name ?? telegram.user?.username ?? '?';

  // The featured QAvatar is rendered as a hero character floating over the
  // banner: bottom-aligned to the banner's bottom edge, top free to extend
  // above the banner. Only opt-in for transparent (RGBA) avatars — full-frame
  // opaque images would just look like a rectangle on top of the banner.
  // Anytime a mesh transition is in flight, drop to the bottom slot so
  // FeaturedAvatar can either render the rotating GLB ('ready') or paint the
  // 2D PNG with the right overlay copy (queued/claimed/meshing/...).
  const meshStatus = featured?.mesh?.status;
  const meshInFlight = meshStatus !== undefined && meshStatus !== 'none' && meshStatus !== 'failed';

  // Phase 7c — if the featured avatar has an active video clip that's ready
  // AND the clip carries transparency (chroma-key today, matting after #185),
  // prefer the animated clip over the static PNG in the floating slot. The
  // ChromaKeyVideo component dissolves the magenta background per frame so
  // the result feels just like the transparent PNG path, just animated.
  const featuredClip =
    featured?.videoClips?.find(c => c.clipId === featured.activeVideoClipId)
    ?? null;
  const featuredClipReady =
    !!featuredClip && featuredClip.status === 'ready' && !!featuredClip.videoUrl;
  const featuredClipHasAlpha =
    featuredClipReady && featuredClip!.transparency && featuredClip!.transparency !== 'none';

  const showFloatingVideo = featuredClipHasAlpha && !meshInFlight;
  const showFloatingImage =
    !showFloatingVideo
    && !!featured?.imageUrl
    && !!featured?.hasAlpha
    && !meshInFlight;
  const showFloatingAvatar = showFloatingVideo || showFloatingImage;

  return (
    <div
      // touch-action: none blocks any pan/zoom/swipe gesture inside the Hub
      // so the screen is completely fixed — no rubber-band, no pull-to-refresh,
      // no double-tap zoom. Other routes (Tournaments, News, etc.) keep their
      // default touch behaviour because this only applies here.
      className="flex h-full flex-col gap-3 overflow-hidden px-4 pb-3 pt-3 [touch-action:none]"
    >
      {/* QBank gamification HUD (coins + currencies + EXP) — account-wide,
          self-hides when the qbank flag is off / no wallet. Compact pill,
          anchored top-right above the banner. */}
      <QbankHud />

      <div className="relative">
        <UserHeaderBanner
          userId={user?.id}
          avatarUrl={user?.avatarUrl}
          avatarFallback={fallback.charAt(0)}
          hideAvatar={showFloatingAvatar}
          onCustomize={() => setCustomizeOpen(true)}
        />
        {showFloatingAvatar && (
          <Link
            to="/avatars"
            aria-label="Open avatars"
            // Floating slot: anchored to banner bottom, extends 180% of the
            // banner height upward. For video clips the subject's feet are
            // at ~97% of the source frame (3% magenta padding below feet);
            // we shift the wrapper down by translate-y on the inner div so
            // the feet land on the banner bottom edge with a tiny overhang.
            // The static-PNG path keeps its existing object-bottom alignment.
            className="absolute bottom-0 left-[2%] h-[180%] transition-transform active:scale-95"
          >
            {showFloatingVideo ? (
              // Video output is 1:1 (768x768 from fal.ai providers) — drop
              // the previous aspect-[3/4] wrapper that distorted the canvas.
              // The canvas's intrinsic width/height (set per-frame from
              // video.videoWidth/Height) carries the correct ratio, and
              // h-full + w-auto lets the browser preserve it.
              //
              // Positioning math (banner ~3:1, overlay h-[180%]):
              //   canvas width ≈ 60% of banner width
              //   subject left edge in source frame ≈ 14.6%
              //   subject right edge in source frame ≈ 91.1%
              //   feet at ~97.4% (3% magenta padding below)
              //
              // translate-x-[-15%] shifts the canvas leftward by 15% of its
              //   own width ≈ 9% of banner width, pulling the subject's left
              //   edge from ~11% of banner width to ~2% — flush with the
              //   left edge the static PNG path uses.
              // translate-y-[2%]  pushes down by 2% of overlay height ≈ 3.6%
              //   of banner height — just enough to consume the source
              //   frame's 3% sub-feet padding so the feet land on the banner
              //   bottom without overshooting below it.
              <div className="h-full w-auto translate-x-[-30%] translate-y-[-4%] drop-shadow-[0_8px_16px_rgba(0,0,0,0.4)]">
                {featuredClip!.matteRgbUrl && featuredClip!.matteAlphaUrl ? (
                  // H.264 dual-stream — universal. Edge decomposite removes
                  // the backdrop tint from semi-transparent edges. Legacy
                  // clips carry chromaKeyHex (magenta); new clips don't
                  // (veed pre-mattes RGB to black) so we default to black.
                  <DualStreamVideo
                    key={`dual-${featuredClip!.clipId}`}
                    rgbUrl={featuredClip!.matteRgbUrl}
                    alphaUrl={featuredClip!.matteAlphaUrl}
                    poster={featuredClip!.posterUrl ?? featured!.imageUrl}
                    knownBgHex={
                      // Veo always runs matting without magenta preprocessing,
                      // so a stale chromaKeyHex would mis-decomposite the
                      // border pixels and amplify scene-color residue into a
                      // halo. Force #000000 for Veo. See AvatarDetail
                      // effectiveBgHexFor() for the matching logic.
                      (featuredClip!.provider === 'veo31-fal' || featuredClip!.provider === 'veo31-fast-fal')
                        ? '#000000'
                        : (featuredClip!.chromaKeyHex ?? '#000000')
                    }
                  />
                ) : featuredClip!.webmAlphaUrl && supportsVp9Alpha() ? (
                  // Legacy WebM VP9-alpha — Chromium only.
                  <video
                    key={`webm-${featuredClip!.clipId}`}
                    src={featuredClip!.webmAlphaUrl}
                    poster={featuredClip!.posterUrl ?? featured!.imageUrl}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <ChromaKeyVideo
                    // key={clipId} forces a clean remount when the user
                    // switches active clips. Without it React reuses the same
                    // <video> element and the browser doesn't auto-reload on
                    // src changes — Hub keeps playing the old MP4.
                    key={featuredClip!.clipId}
                    src={featuredClip!.videoUrl!}
                    poster={featuredClip!.posterUrl ?? featured!.imageUrl}
                    keyColor={featuredClip!.chromaKeyHex ?? '#FF00FF'}
                  />
                )}
              </div>
            ) : (
              <img
                src={featured!.imageUrl}
                alt=""
                className="h-full w-auto object-contain object-bottom drop-shadow-[0_8px_16px_rgba(0,0,0,0.4)]"
              />
            )}
          </Link>
        )}
      </div>

      {/* Game selector + identity — sits under the avatar/banner (left-aligned).
          Tap the icon to switch games. The QLASH account name carries the
          accent colour; the game-specific account stays neutral. */}
      <GameHeader />

      <CustomizeSheet
        userId={user?.id}
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
      />
      {queueOpen && (
        <QueueSheet open={queueOpen} onClose={() => setQueueOpen(false)} />
      )}

      {/* Cross-game pills — always present regardless of active game. Riot and
          QChess moved into the game-conditional section below (QChess is now a
          selectable game via the header picker). */}
      <div className="flex flex-wrap items-center gap-3">
        <LeaderboardCart />
        <WatchPartyHub />
      </div>

      {/* ── Game-conditional section ──────────────────────────────────────
          Renders the active game's features only. Clash Royale → two direct
          buttons (Decks / Battles). A Riot game → the Riot section. No active
          game → nothing here. */}
      {showCr && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Link
              to="/cr/decks"
              className="flex flex-col gap-1 rounded-2xl border border-brand/40 bg-brand/5 px-4 py-3 text-sm transition-opacity active:opacity-80"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/15 text-brand">
                <Layers aria-hidden className="h-5 w-5" strokeWidth={2.5} />
              </span>
              <span className="font-bold text-fg">Mazzi</span>
              <span className="text-[11px] text-fg-muted">Decks &amp; AI coaching</span>
            </Link>
            <Link
              to="/cr/battles"
              className="flex flex-col gap-1 rounded-2xl border border-brand/40 bg-brand/5 px-4 py-3 text-sm transition-opacity active:opacity-80"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/15 text-brand">
                <BattleIcon aria-hidden className="h-5 w-5" strokeWidth={2.5} />
              </span>
              <span className="font-bold text-fg">Battaglie</span>
              <span className="text-[11px] text-fg-muted">Recent matches</span>
            </Link>
          </div>

          {/* ⚔️ 1v1 — open matchmaking (Trova partita) + friend challenge
              (Sfida). Trova partita opens the searching sheet; Sfida deep-links
              into the Friends → Sfide sub-tab. */}
          <div className="rounded-2xl border border-brand/40 bg-brand/5 px-4 py-3">
            <div className="mb-2 flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/15 text-brand">
                <Swords aria-hidden className="h-5 w-5" strokeWidth={2.5} />
              </span>
              <span className="font-bold text-fg">1v1</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setQueueOpen(true)}
                className="rounded-xl bg-brand py-2.5 text-sm font-bold text-brand-fg transition-opacity active:opacity-80"
              >
                {t('queue.find', { defaultValue: 'Find match' })}
              </button>
              <button
                type="button"
                onClick={() => navigate('/friends?tab=duels')}
                className="rounded-xl bg-bg-tertiary py-2.5 text-sm font-bold text-fg-muted transition-opacity active:opacity-80"
              >
                {t('social.challenge', { ns: 'general', defaultValue: 'Challenge' })}
              </button>
            </div>
          </div>
        </>
      )}

      {showRiot && (
        <div className="flex flex-wrap items-center gap-3">
          <RiotHubTile />
        </div>
      )}

      {showChess && <ChessHubSection />}

      {showCoc && <CocHubSection />}

      {/* Avatar entry — cross-game (gated only by the avatars flag). Replaces
          the old standalone Avatar Editor CTA, which now lives inside the
          /avatars screen. */}
      {env.featureAvatarsEnabled && (
        <Link
          to="/avatars"
          className="flex items-center gap-3 rounded-2xl border border-brand/40 bg-brand/5 px-4 py-3 text-sm transition-opacity active:opacity-80"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand/15 text-brand">
            <Sparkles aria-hidden className="h-5 w-5" strokeWidth={2.5} />
          </span>
          <div className="flex flex-col">
            <span className="font-bold text-fg">Avatar</span>
            <span className="text-[11px] text-fg-muted">Your AI-generated QLASH identity</span>
          </div>
        </Link>
      )}

      {/* Bottom hero avatar — fallback entry point to /avatars when there is
          no transparent QAvatar to render as the banner overlay. When a
          transparent featured avatar exists, the banner overlay takes its
          place and this region collapses. */}
      {env.featureAvatarsEnabled && !showFloatingAvatar && (
        <FeaturedAvatar className="min-h-0 flex-1" />
      )}
    </div>
  );
}
